import {
  Button,
  ModalBody,
  ModalCloseButton,
  ModalHeader,
  ModalOverlay,
  useBreakpointValue,
  useDisclosure,
  VStack,
  HStack,
  Text,
  Tag,
} from "@chakra-ui/react";
import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  useMemo,
  memo,
} from "react";
import { FiSliders } from "react-icons/fi";
import { OrderFilterSetting } from "../../../../../types/order/orderQuery";
import { AppModal } from "../../../../modals/AppModal";
import { AppModalContent } from "../../../../modals/AppModalContent";
import { AppModalFooter } from "../../../../modals/AppModalFooter";
import { ImportFilterSelector } from "../../ImportFilterSelector";
import { SearchSection } from "../SearchSection";
import { defaultOrderFilterState } from "../TransactionsView";
import { AssetFilter } from "./AssetFilter";
import { DataSourceFilter } from "./DataSourceFilter";
import { DateFilter } from "./DateFilter";
import { hasActiveFilters } from "./hasActiveFilters";
import { LedgerTypeFilter } from "./LedgerTypeFilter";
import { StatusFilter } from "./StatusFilter";

export const FilterSection = memo(function FilterSection({
  filterSetting,
  setFilterSetting,
}: {
  filterSetting: OrderFilterSetting;
  setFilterSetting: Dispatch<SetStateAction<OrderFilterSetting>>;
}): React.ReactElement {
  const isFiltered = useIsFiltered(filterSetting);

  const isMobile = useBreakpointValue({
    base: true,
    sm: false,
  });

  const clearFilters = () => {
    setFilterSetting(defaultOrderFilterState);
  };

  const filterControls = (
    <FilterControls
      filterSetting={filterSetting}
      setFilterSetting={setFilterSetting}
      isMobile={isMobile}
    />
  );

  if (isMobile) {
    return (
      <MobileFilterLayout isFiltered={isFiltered} onClearFilters={clearFilters}>
        {filterControls}
      </MobileFilterLayout>
    );
  }

  return (
    <DesktopFilterLayout isFiltered={isFiltered} onClearFilters={clearFilters}>
      {filterControls}
    </DesktopFilterLayout>
  );
});

function DesktopFilterLayout({
  children,
  isFiltered,
  onClearFilters,
}: {
  children: ReactNode;
  isFiltered: boolean;
  onClearFilters: () => void;
}): React.ReactElement {
  return (
    <>
      {children}

      {isFiltered && (
        <Button
          flexShrink={0}
          variant="ghost"
          colorScheme="red"
          onClick={onClearFilters}
          size="sm"
        >
          Clear
        </Button>
      )}
    </>
  );
}

function MobileFilterLayout({
  children,
  isFiltered,
  onClearFilters,
}: {
  children: ReactNode;
  isFiltered: boolean;
  onClearFilters: () => void;
}): React.ReactElement {
  const disclosure = useDisclosure();

  return (
    <>
      <Button
        variant="outline"
        onClick={disclosure.onOpen}
        size="sm"
        backgroundColor="white"
        justifyContent="flex-start"
      >
        <HStack spacing={2}>
          <FiSliders />
          <Text>Filters</Text>
          {isFiltered && (
            <Tag
              size="sm"
              colorScheme="primary"
              variant="subtle"
              borderRadius="full"
            >
              {"✓"}
            </Tag>
          )}
        </HStack>
      </Button>

      <AppModal
        isOpen={disclosure.isOpen}
        onClose={disclosure.onClose}
        size="full"
      >
        <ModalOverlay />

        <AppModalContent>
          <ModalHeader>Filters</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <VStack align="stretch" spacing={4}>
              {children}
            </VStack>
          </ModalBody>

          <AppModalFooter>
            {isFiltered && (
              <Button
                flexShrink={0}
                variant="ghost"
                colorScheme="red"
                onClick={onClearFilters}
              >
                Clear
              </Button>
            )}

            <Button colorScheme="primary" onClick={disclosure.onClose}>
              Done
            </Button>
          </AppModalFooter>
        </AppModalContent>
      </AppModal>
    </>
  );
}

export function FilterControls({
  filterSetting,
  setFilterSetting,
  isMobile,
}: {
  filterSetting: OrderFilterSetting;
  setFilterSetting: Dispatch<SetStateAction<OrderFilterSetting>>;
  isMobile?: boolean;
}): React.ReactElement {
  return (
    <>
      <SearchSection
        justifySelf="flex-start"
        autoFocus
        backgroundColor="white"
        minW="5em"
        maxW={{ base: undefined, sm: "10em" }}
        w={{ base: "full", sm: "auto" }}
        filterSetting={filterSetting}
        setFilterSetting={setFilterSetting}
        size={isMobile ? "md" : "sm"}
      />

      <LedgerTypeFilter
        values={filterSetting.ledgerType}
        onChange={(values) => {
          setFilterSetting((oldState) => ({
            ...oldState,
            ledgerType: values,
          }));
        }}
        size={isMobile ? "md" : "sm"}
        buttonProps={{ backgroundColor: "white", flexShrink: 0 }}
        popoverProps={{ matchWidth: isMobile }}
      />

      <DataSourceFilter
        values={filterSetting.dataSource}
        onChange={(values) => {
          setFilterSetting((oldState) => ({
            ...oldState,
            dataSource: values,
          }));
        }}
        size={isMobile ? "md" : "sm"}
        buttonProps={{ backgroundColor: "white", flexShrink: 0 }}
        popoverProps={{ matchWidth: isMobile }}
      />

      <AssetFilter
        values={filterSetting.asset}
        onChange={(values) => {
          setFilterSetting((oldState) => ({
            ...oldState,
            asset: values,
          }));
        }}
        enableDoubleColumn={!isMobile}
        size={isMobile ? "md" : "sm"}
        popoverProps={{
          matchWidth: isMobile,
        }}
        buttonProps={{ backgroundColor: "white", flexShrink: 0 }}
      />

      <ImportFilterSelector
        values={filterSetting.imports}
        onChange={(values) =>
          setFilterSetting((oldState) => ({ ...oldState, imports: values }))
        }
        size={isMobile ? "md" : "sm"}
        buttonProps={{
          backgroundColor: "white",
          flexShrink: 0,
        }}
        popoverProps={{
          matchWidth: isMobile,
        }}
      />

      <StatusFilter
        values={filterSetting.status}
        onChange={(values) => {
          setFilterSetting((oldState) => ({
            ...oldState,
            status: values,
          }));
        }}
        size={isMobile ? "md" : "sm"}
        buttonProps={{ backgroundColor: "white", flexShrink: 0 }}
        popoverProps={{
          matchWidth: isMobile,
        }}
      />

      <DateFilter
        value={filterSetting.dateRange}
        onChange={(newDateRange) => {
          setFilterSetting((oldState) => ({
            ...oldState,
            dateRange: newDateRange,
          }));
        }}
        buttonProps={{
          size: isMobile ? "md" : "sm",
          backgroundColor: "white",
          flexShrink: 0,
        }}
        popoverProps={{
          matchWidth: isMobile,
        }}
      />
    </>
  );
}

function useIsFiltered(filterSetting: OrderFilterSetting): boolean {
  return useMemo(() => {
    return hasActiveFilters(filterSetting);
  }, [filterSetting]);
}
